import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CheckCircle, Circle, ArrowRight, Sparkle } from "@phosphor-icons/react";

// Trial-only "Setup X% complete" chip. Frontend-only: progress is derived entirely from
// existing read state (GET /me/subscription usage counts + client-side localStorage flags).
// No new APIs, no persistence, no backend/auth/billing changes.
export function SetupProgressChip() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const uid = user?.user_id || user?.email || "u";
  const [sub, setSub] = useState(null);
  const [, setTick] = useState(0); // forces a re-read of localStorage-backed steps

  const fetchSub = useCallback(() => {
    api.get("/me/subscription").then((r) => setSub(r.data)).catch(() => setSub(null));
  }, []);

  useEffect(() => {
    fetchSub();
    const refresh = () => { fetchSub(); setTick((t) => t + 1); };
    const reread = () => setTick((t) => t + 1);
    window.addEventListener("recoveros:usage-changed", refresh);
    window.addEventListener("recoveros:setup-changed", refresh);
    window.addEventListener("storage", reread);
    window.addEventListener("focus", reread);
    return () => {
      window.removeEventListener("recoveros:usage-changed", refresh);
      window.removeEventListener("recoveros:setup-changed", refresh);
      window.removeEventListener("storage", reread);
      window.removeEventListener("focus", reread);
    };
  }, [fetchSub]);

  const trial = !!sub && sub.is_trial;
  const accounts = sub?.accounts_used ?? 0;
  const users = sub?.users_used ?? 1;
  const excelUploaded = localStorage.getItem(`recoveros_excel_uploaded_${uid}`) === "1" || accounts > 0;
  const dashViewed = localStorage.getItem(`recoveros_dashboard_viewed_${uid}`) === "1";

  const steps = [
    { key: "account", label: "Account created", done: true },
    { key: "email", label: "Email registered", done: !!user?.email },
    { key: "excel", label: "Upload first Excel", done: excelUploaded, to: "/import" },
    { key: "dashboard", label: "View dashboard", done: dashViewed, to: "/dashboard" },
    { key: "team", label: "Invite a team member", done: users > 1, to: "/users" },
    { key: "import", label: "Complete first import", done: accounts > 0, to: "/import" },
  ];
  const completed = steps.filter((s) => s.done).length;
  const pct = trial ? Math.round((completed / steps.length) * 100) : 0;

  // One-time celebration the moment a trial workspace hits 100% setup.
  useEffect(() => {
    if (!trial || pct < 100) return;
    const key = `recoveros_setup_celebrated_${uid}`;
    if (localStorage.getItem(key) === "1") return;
    localStorage.setItem(key, "1");
    toast.success("🎉 Setup complete — your workspace is fully set up!", { duration: 6000 });
    const end = Date.now() + 900;
    const colors = ["#0B3D91", "#16A34A", "#7ef0a9"];
    (function frame() {
      confetti({ particleCount: 4, angle: 60, spread: 55, startVelocity: 45, origin: { x: 0 }, colors });
      confetti({ particleCount: 4, angle: 120, spread: 55, startVelocity: 45, origin: { x: 1 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  }, [trial, pct, uid]);

  if (!trial) return null;
  if (pct >= 100) return null; // auto-hide once fully set up

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="setup-progress-chip"
          className="hidden sm:inline-flex items-center gap-2 rounded-full border border-[#16A34A]/30 bg-[#16A34A]/[0.07] pl-2.5 pr-3 py-1.5 hover:bg-[#16A34A]/[0.12] transition-colors"
        >
          <span className="relative inline-flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#16A34A] opacity-60 animate-ping" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#16A34A]" />
          </span>
          <span className="text-xs font-bold text-[#15803d] whitespace-nowrap">Setup {pct}% complete</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 rounded-sm p-0" data-testid="setup-progress-popover">
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center gap-1.5 font-heading font-bold text-sm">
            <Sparkle size={16} weight="fill" className="text-[#0B3D91]" /> Getting started
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{completed} of {steps.length} complete — finish setup to get the most out of your trial.</p>
          <div className="mt-2 h-1.5 w-full bg-border rounded-full overflow-hidden">
            <div className="h-full bg-[#16A34A] transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className="py-1">
          {steps.map((s) => (
            <button
              key={s.key}
              disabled={s.done || !s.to}
              onClick={() => s.to && navigate(s.to)}
              data-testid={`setup-step-${s.key}`}
              className={`w-full flex items-center gap-2.5 px-4 py-2 text-left text-sm ${!s.done && s.to ? "hover:bg-secondary cursor-pointer" : "cursor-default"}`}
            >
              {s.done
                ? <CheckCircle size={17} weight="fill" className="text-[#16A34A] shrink-0" />
                : <Circle size={17} className="text-muted-foreground/40 shrink-0" />}
              <span className={s.done ? "text-muted-foreground line-through" : "font-medium"}>{s.label}</span>
              {!s.done && s.to && <ArrowRight size={13} weight="bold" className="ml-auto text-[#0B3D91]" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
